import { api } from './client';
import { ensureStringId } from './helpers';
import type { ApiResponse, User, Order, SupportTicket, PaginationData } from '../types';

export const adminAPI = {
  health: () => api.get<ApiResponse>('/api/admin/health'),
  
  getPlatformStats: () => api.get<ApiResponse<{
    totalUsers: number;
    totalRiders: number;
    totalCustomers: number;
    totalOrders: number;
    totalRevenue: number;
    activeListings: number;
    pendingDisputes: number;
    recentActivity: Array<{
      id: string;
      type: string;
      description: string;
      timestamp: string;
    }>;
  }>>('/api/admin/stats'),
  
  getUserManagement: (params?: { 
    page?: number; 
    limit?: number; 
    role?: string; 
    search?: string;
    status?: string;
    sortBy?: string;
  }) => api.get<ApiResponse<{ 
    users: User[]; 
    pagination: PaginationData;
    total: number;
  }>>('/api/admin/users', { params }),
  
  updateUserStatus: (userId: string, data: { 
    isActive?: boolean; 
    isVerified?: boolean; 
    role?: string;
    reason?: string;
  }) => {
    const idString = ensureStringId(userId);
    return api.put<ApiResponse<{ user: User }>>(`/api/admin/users/${idString}/status`, data);
  },
  
  getOrderManagement: (params?: { 
    page?: number; 
    limit?: number; 
    status?: string; 
    serviceType?: string;
    startDate?: string;
    endDate?: string;
    riderId?: string;
    customerId?: string;
  }) => api.get<ApiResponse<{ 
    orders: Order[]; 
    pagination: PaginationData;
    total: number;
    summary: {
      totalAmount: number;
      platformFees: number;
      riderPayouts: number;
    };
  }>>('/api/admin/orders', { params }),
  
  updateOrderStatus: (orderId: string, data: { status: string; reason?: string }) => {
    const idString = ensureStringId(orderId);
    return api.put<ApiResponse<{ order: Order }>>(`/api/admin/orders/${idString}/status`, data);
  },
  
  getFinancialDashboard: (params?: { 
    startDate?: string; 
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }) => api.get<ApiResponse<{
    totalRevenue: number;
    platformEarnings: number;
    riderPayouts: number;
    transactionCount: number;
    breakdown: Array<{
      date: string;
      revenue: number;
      orders: number;
      fees: number;
    }>;
    currency: string;
  }>>('/api/admin/financial', { params }),
  
  getDisputes: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<ApiResponse<{ 
      disputes: SupportTicket[]; 
      pagination: PaginationData 
    }>>('/api/admin/disputes', { params }),
  
  resolveDispute: (ticketId: string, data: { resolution: string; action?: string }) => {
    const idString = ensureStringId(ticketId);
    return api.post<ApiResponse<{ ticket: SupportTicket }>>(`/api/admin/disputes/${idString}/resolve`, data);
  },
  
  getSystemHealth: () =>
    api.get<ApiResponse<{
      status: 'healthy' | 'degraded' | 'down';
      services: Record<string, 'up' | 'down'>;
      metrics: {
        responseTime: number;
        errorRate: number;
        requestsPerMinute: number;
      };
    }>>('/api/admin/system/health'),
  
  getAuditLogs: (params?: { page?: number; limit?: number; userId?: string; action?: string }) =>
    api.get<ApiResponse<{ 
      logs: Array<{
        id: string;
        userId: string;
        action: string;
        details: Record<string, unknown>;
        ip: string;
        timestamp: string;
      }>; 
      pagination: PaginationData 
    }>>('/api/admin/audit-logs', { params }),
};

export default adminAPI;
